import { useSyncExternalStore } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import type { CheckStatus, PresignedUpload } from '@keetcheck/shared';
import { apiFor, ApiError } from '../api.js';
import { emitError } from '../toastBus.js';
import { idbAll, idbClear, idbDelete, idbSet } from './idb.js';
import type { CheckItemDetail, CheckRunDetail } from '../queries.js';

/**
 * Check changes go through this outbox, online or not. Each tap is kept on
 * the phone (IndexedDB) and shown straight away, then sent in order; with
 * no signal they wait and go when it's back. A change to an item someone
 * else has changed since this phone last saw it is refused by the server
 * and shown as a conflict rather than overwriting theirs.
 */

export interface ItemFields {
  status?: CheckStatus; note?: string; externalRef?: string; flagged?: boolean; reading?: string;
}

interface OpBase { id: string; orgId: string; runId: string; createdAt: number; attempts?: number }
export type Op =
  | OpBase & { kind: 'item'; itemId: string; label: string; fields: ItemFields; base: string | null }
  | OpBase & { kind: 'mark'; itemIds: string[]; status: CheckStatus }
  | OpBase & { kind: 'photo'; itemId: string; label: string; blob: Blob; name: string; contentType: string;
      /** marked up: the photo as taken, kept beside it */
      original?: Blob; originalContentType?: string };

export interface Conflict {
  id: string; orgId: string; runId: string; itemId: string; label: string;
  /** what the server said, e.g. "Bob changed this while you were offline" */
  message: string;
  mine: ItemFields;
}

interface State {
  ops: Op[];
  conflicts: Conflict[];
  /** sending right now */
  syncing: boolean;
  /** the last try didn't reach the server (no signal, or signed-out session) */
  stalled: boolean;
  online: boolean;
  loaded: boolean;
}

let state: State = { ops: [], conflicts: [], syncing: false, stalled: false, online: navigator.onLine, loaded: false };
const listeners = new Set<() => void>();
let qc: QueryClient | null = null;
let inFlight: string | null = null;
let timer: ReturnType<typeof setTimeout> | undefined;

function set(patch: Partial<State>) {
  state = { ...state, ...patch };
  listeners.forEach((l) => l());
}

const newId = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
const saveOp = (op: Op) => idbSet('outbox', `op:${op.id}`, op);
const saveConflict = (c: Conflict) => idbSet('outbox', `conflict:${c.id}`, { ...c, kind: 'conflict' });

/** Load what was waiting when the app last closed, and start syncing. */
export async function initOutbox(client: QueryClient): Promise<void> {
  qc = client;
  const rows = await idbAll<(Op | (Conflict & { kind: 'conflict' }))>('outbox');
  const ops = rows.filter((r): r is Op => r.kind !== 'conflict').sort((a, b) => a.createdAt - b.createdAt);
  const conflicts = rows.filter((r): r is Conflict & { kind: 'conflict' } => r.kind === 'conflict');
  set({ ops: [...ops, ...state.ops], conflicts: [...conflicts, ...state.conflicts], loaded: true });
  window.addEventListener('online', () => { set({ online: true }); void flush(); });
  window.addEventListener('offline', () => set({ online: false }));
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') void flush(); });
  void flush();
}

// ── Adding changes ────────────────────────────────────────────

/** A change to one item. Waiting changes to the same item are merged into one. */
export function enqueueItem(o: { orgId: string; runId: string; itemId: string; label: string; fields: ItemFields; base: string | null }) {
  const fields = Object.fromEntries(Object.entries(o.fields).filter(([, v]) => v !== undefined)) as ItemFields;
  const waiting = state.ops.find((x): x is Extract<Op, { kind: 'item' }> => x.kind === 'item' && x.itemId === o.itemId && x.id !== inFlight);
  if (waiting) {
    const merged = { ...waiting, fields: { ...waiting.fields, ...fields } };
    set({ ops: state.ops.map((x) => (x.id === waiting.id ? merged : x)) });
    void saveOp(merged);
  } else {
    const op: Op = { kind: 'item', id: newId(), createdAt: Date.now(), ...o, fields };
    set({ ops: [...state.ops, op] });
    void saveOp(op);
  }
  void flush();
}

export function enqueueMark(o: { orgId: string; runId: string; itemIds: string[]; status: CheckStatus }) {
  const op: Op = { kind: 'mark', id: newId(), createdAt: Date.now(), ...o };
  set({ ops: [...state.ops, op] });
  void saveOp(op);
  void flush();
}

export function enqueuePhoto(o: {
  orgId: string; runId: string; itemId: string; label: string; blob: Blob; name: string; contentType: string;
  original?: Blob; originalContentType?: string;
}) {
  const op: Op = { kind: 'photo', id: newId(), createdAt: Date.now(), ...o };
  set({ ops: [...state.ops, op] });
  void saveOp(op);
  void flush();
}

/** Take back a photo that hasn't been sent yet. */
export function removePendingPhoto(opId: string) {
  if (opId === inFlight) return;
  dropOp(opId);
  refresh(state.ops.find((x) => x.id === opId));
}

// ── Sending ───────────────────────────────────────────────────

let flushing: Promise<void> | null = null;

export function flush(): Promise<void> {
  flushing ??= doFlush().finally(() => { flushing = null; });
  return flushing;
}

async function doFlush() {
  if (!qc || !state.ops.length) return;
  clearTimeout(timer);
  set({ syncing: true });
  while (state.ops.length) {
    const op = state.ops[0]!;
    inFlight = op.id;
    try {
      const result = await send(op);
      dropOp(op.id);
      applied(op, result);
      set({ stalled: false });
    } catch (e) {
      if (!(e instanceof ApiError)) {
        // No signal (or the sign-in needs refreshing online): try again later.
        set({ stalled: true });
        break;
      }
      if (e.code === 'ITEM_CHANGED' && op.kind === 'item') {
        const c: Conflict = { id: op.id, orgId: op.orgId, runId: op.runId, itemId: op.itemId, label: op.label, message: e.message, mine: op.fields };
        set({ conflicts: [...state.conflicts, c] });
        void saveConflict(c);
        dropOp(op.id);
      } else if (e.status >= 500 && (op.attempts ?? 0) < 8) {
        const retried = { ...op, attempts: (op.attempts ?? 0) + 1 };
        set({ ops: state.ops.map((x) => (x.id === op.id ? retried : x)) });
        void saveOp(retried);
        break;
      } else {
        // The server won't take it (e.g. the check was submitted meanwhile).
        emitError(`${'label' in op ? `${op.label}: ` : ''}${e.message}`);
        dropOp(op.id);
      }
      refresh(op);
    } finally {
      inFlight = null;
    }
  }
  set({ syncing: false });
  if (state.ops.length) timer = setTimeout(() => void flush(), state.online ? 15_000 : 60_000);
}

async function send(op: Op): Promise<unknown> {
  const api = apiFor(op.orgId);
  if (op.kind === 'item') {
    return api.patch<CheckItemDetail>(`/check-items/${op.itemId}`, { ...op.fields, ...(op.base ? { baseUpdatedAt: op.base } : {}) });
  }
  if (op.kind === 'mark') {
    return api.post(`/orgs/${op.orgId}/check-runs/${op.runId}/mark`, { itemIds: op.itemIds, status: op.status });
  }
  // Photo: the record first, then the file straight to storage. A failed
  // upload takes the record away again, and the photo stays queued.
  const presign = await api.post<PresignedUpload>('/photos/upload-url', {
    itemId: op.itemId, filename: op.name, contentType: op.contentType,
    ...(op.original && op.originalContentType ? { originalContentType: op.originalContentType } : {}),
  });
  const put = (url: string, body: Blob, type: string) =>
    fetch(url, { method: 'PUT', body, headers: { 'Content-Type': type } }).then((r) => r.ok).catch(() => false);
  const okMarked = await put(presign.uploadUrl, op.blob, op.contentType);
  const okOriginal = !presign.originalUploadUrl || (await put(presign.originalUploadUrl, op.original!, op.originalContentType!));
  if (!okMarked || !okOriginal) {
    await api.delete(`/photos/${presign.photo.id}`).catch(() => {});
    throw new Error('Photo upload interrupted');
  }
  return presign.photo;
}

function dropOp(id: string) {
  set({ ops: state.ops.filter((x) => x.id !== id) });
  void idbDelete('outbox', `op:${id}`);
  const url = photoUrls.get(id);
  if (url) { URL.revokeObjectURL(url); photoUrls.delete(id); }
}

/** After a change lands: show the server's version (and carry it forward as the base). */
function applied(op: Op, result: unknown) {
  if (op.kind === 'item') {
    const saved = result as CheckItemDetail;
    set({ ops: state.ops.map((x) => (x.kind === 'item' && x.itemId === op.itemId ? { ...x, base: saved.updatedAt } : x)) });
    qc?.setQueryData<CheckRunDetail>(['orgs', op.orgId, 'check-runs', op.runId], (run) => run && {
      ...run,
      items: run.items.map((it) => (it.id === op.itemId ? overlayOne({
        ...it, status: saved.status, note: saved.note, externalRef: saved.externalRef, flagged: saved.flagged,
        reading: saved.reading, checkedAt: saved.checkedAt, checkedBy: saved.checkedBy, updatedAt: saved.updatedAt,
      }) : it)),
    });
    void qc?.invalidateQueries({ queryKey: ['check-items', op.itemId] });
  } else {
    refresh(op);
  }
}

function refresh(op: Op | undefined) {
  if (!op || !qc) return;
  void qc.invalidateQueries({ queryKey: ['orgs', op.orgId, 'check-runs', op.runId] });
  if (op.kind !== 'mark') void qc.invalidateQueries({ queryKey: ['check-items', op.itemId] });
}

// ── Conflicts ─────────────────────────────────────────────────

export function dismissConflict(id: string) {
  set({ conflicts: state.conflicts.filter((c) => c.id !== id) });
  void idbDelete('outbox', `conflict:${id}`);
}

/** "Use mine": send my version over theirs. */
export function keepMine(c: Conflict) {
  dismissConflict(c.id);
  enqueueItem({ orgId: c.orgId, runId: c.runId, itemId: c.itemId, label: c.label, fields: c.mine, base: null });
}

// ── Showing waiting changes on top of what the server says ───

const photoUrls = new Map<string, string>();

function itemOps(itemId: string) {
  return state.ops.filter((x) => x.kind === 'item' && x.itemId === itemId) as Extract<Op, { kind: 'item' }>[];
}

function overlayOne<T extends { id: string; status: CheckStatus | null; photoCount?: number }>(it: T): T {
  let out: T = it;
  for (const op of state.ops) {
    if (op.kind === 'item' && op.itemId === it.id) out = { ...out, ...op.fields };
    if (op.kind === 'mark' && op.itemIds.includes(it.id) && out.status == null) out = { ...out, status: op.status };
    if (op.kind === 'photo' && op.itemId === it.id && out.photoCount !== undefined) out = { ...out, photoCount: (out.photoCount ?? 0) + 1 };
  }
  return out;
}

/** A check's items with this phone's waiting changes applied. */
export function overlayRun(run: CheckRunDetail): CheckRunDetail {
  if (!state.ops.some((x) => x.runId === run.id)) return run;
  return { ...run, items: run.items.map((it) => overlayOne(it)) };
}

export interface PendingPhoto { id: string; url: string; pending: true }

/** An item with this phone's waiting changes and not-yet-sent photos. */
export function overlayItem(item: CheckItemDetail): CheckItemDetail & { pendingPhotos: PendingPhoto[] } {
  let out = item;
  for (const op of itemOps(item.id)) out = { ...out, ...op.fields };
  const pendingPhotos = state.ops
    .filter((x): x is Extract<Op, { kind: 'photo' }> => x.kind === 'photo' && x.itemId === item.id)
    .map((op) => {
      let url = photoUrls.get(op.id);
      if (!url) { url = URL.createObjectURL(op.blob); photoUrls.set(op.id, url); }
      return { id: op.id, url, pending: true as const };
    });
  return { ...out, pendingPhotos };
}

/** The version of an item this phone last saw, to send along with a change. */
export function baseOf(item: { updatedAt?: string } | undefined): string | null {
  return item?.updatedAt ?? null;
}

// ── For screens ───────────────────────────────────────────────

const subscribe = (l: () => void) => { listeners.add(l); return () => listeners.delete(l); };
const snapshot = () => state;

export function useOutbox(): State {
  return useSyncExternalStore(subscribe, snapshot);
}

export const pendingForRun = (s: State, runId: string) => s.ops.filter((x) => x.runId === runId).length;
export const hasPending = () => state.ops.length > 0;

/** Signing out: nothing of this person's may be sent under the next one's sign-in. */
export async function clearOutbox(): Promise<void> {
  for (const url of photoUrls.values()) URL.revokeObjectURL(url);
  photoUrls.clear();
  set({ ops: [], conflicts: [], stalled: false });
  await idbClear('outbox');
}
